import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { LOGIN } from '../graphql/adminQueries';
import { useAuth } from './AuthContext';
import EnvironmentBanner from '../components/EnvironmentBanner';

export const OTP_LENGTH = 6;

export const onlyOtpDigits = (value) =>
  String(value || '')
    .replace(/\D/g, '')
    .slice(0, OTP_LENGTH);

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser, user, isAdmin, isStaff } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [loginStep, setLoginStep] = useState('password');
  const [submitError, setSubmitError] = useState(null);
  const [loading, setLoading] = useState(false);
  const mfaSubmittingRef = useRef(false);
  const completedTotpRef = useRef('');

  const reason = searchParams.get('reason');
  const reasonMessage =
    reason === 'access_denied'
      ? 'This account does not have admin console access.'
      : reason === 'mfa_required'
        ? 'Admin accounts must have MFA enabled before using the ops console (required on staging/production).'
        : null;

  useEffect(() => {
    if (user && (isAdmin || isStaff)) {
      navigate('/', { replace: true });
    }
  }, [user, isAdmin, isStaff, navigate]);

  const [login] = useMutation(LOGIN, {
    onCompleted: (data) => {
      setLoading(false);
      mfaSubmittingRef.current = false;
      const signedInUser = data?.signIn?.user;
      if (!signedInUser) {
        setSubmitError('This account does not have admin console access.');
        return;
      }
      setUser(signedInUser);
      navigate('/economics', { replace: true });
    },
    onError: (error) => {
      setLoading(false);
      mfaSubmittingRef.current = false;
      const code = error?.graphQLErrors?.[0]?.extensions?.code;
      if (code === 'MFA_REQUIRED') {
        setLoginStep('mfa');
        setSubmitError(null);
        completedTotpRef.current = '';
        return;
      }
      if (code === 'MFA_REQUIRED_FOR_ADMIN') {
        setSubmitError(
          'Admin access requires MFA. Enable MFA on your account before signing in here.'
        );
      } else if (code === 'INVALID_MFA_TOKEN') {
        setSubmitError('Invalid MFA token or backup code. Please try again.');
        completedTotpRef.current = '';
      } else if (code === 'UNAUTHENTICATED' || code === 'INVALID_CREDENTIALS') {
        setSubmitError('Invalid email or password. Please try again.');
      } else if (error?.networkError) {
        setSubmitError('Network error. Check your connection and try again.');
      } else {
        setSubmitError('An unexpected error occurred. Please try again.');
      }
    },
  });

  const submitLogin = (codeValue = mfaCode) => {
    if (mfaSubmittingRef.current) return;

    const variables = {
      email: email.trim(),
      password,
    };

    if (loginStep === 'mfa') {
      const trimmed = String(codeValue ?? '').trim();
      if (useBackupCode) {
        if (!trimmed) {
          setSubmitError('Please enter your backup code.');
          return;
        }
        variables.mfaBackupCode = trimmed;
      } else {
        const digits = onlyOtpDigits(trimmed);
        if (digits.length !== OTP_LENGTH) {
          setSubmitError('Please enter your 6-digit code.');
          return;
        }
        variables.mfaToken = digits;
      }
    }

    mfaSubmittingRef.current = true;
    setSubmitError(null);
    setLoading(true);
    login({ variables });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    submitLogin();
  };

  const applyTotpCode = (next) => {
    const code = onlyOtpDigits(next);
    setMfaCode(code);
    if (submitError) setSubmitError(null);
    if (code.length === OTP_LENGTH) {
      if (completedTotpRef.current !== code) {
        completedTotpRef.current = code;
        submitLogin(code);
      }
    } else {
      completedTotpRef.current = '';
    }
  };

  const handleMfaChange = (event) => {
    if (useBackupCode) {
      setMfaCode(event.target.value);
      if (submitError) setSubmitError(null);
      return;
    }
    applyTotpCode(event.target.value);
  };

  const handleMfaPaste = (event) => {
    if (useBackupCode) return;
    const text = event.clipboardData?.getData('text') || '';
    const pasted = onlyOtpDigits(text);
    if (!pasted) return;
    event.preventDefault();
    applyTotpCode(pasted);
  };

  return (
    <div className="login-shell">
      <EnvironmentBanner />
      <main className="login-main">
        <section className="login-card">
          <h1>Fiscal Flowcast Ops</h1>
          <p className="login-subtitle">Sign in with an ADMIN or STAFF account.</p>

          {reasonMessage && (
            <p className="login-alert" role="alert">
              {reasonMessage}
            </p>
          )}
          {submitError && (
            <p className="login-alert login-alert-error" role="alert">
              {submitError}
            </p>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            {loginStep === 'password' ? (
              <>
                <label htmlFor="admin-email">Email</label>
                <input
                  id="admin-email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
                <label htmlFor="admin-password">Password</label>
                <input
                  id="admin-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
              </>
            ) : (
              <>
                <p className="login-mfa-hint">
                  Enter your {useBackupCode ? 'backup code' : 'authenticator code'}.
                </p>
                <label htmlFor="admin-mfa">{useBackupCode ? 'Backup code' : 'MFA code'}</label>
                <input
                  id="admin-mfa"
                  name={useBackupCode ? 'mfaBackupCode' : 'mfaCode'}
                  type="text"
                  inputMode={useBackupCode ? 'text' : 'numeric'}
                  pattern={useBackupCode ? undefined : '[0-9]*'}
                  autoComplete={useBackupCode ? 'off' : 'one-time-code'}
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  autoFocus
                  maxLength={useBackupCode ? undefined : OTP_LENGTH}
                  value={mfaCode}
                  onChange={handleMfaChange}
                  onPaste={handleMfaPaste}
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    setUseBackupCode((prev) => !prev);
                    setMfaCode('');
                    completedTotpRef.current = '';
                    mfaSubmittingRef.current = false;
                    setSubmitError(null);
                  }}
                >
                  {useBackupCode ? 'Use authenticator code' : 'Use backup code instead'}
                </button>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    setLoginStep('password');
                    setMfaCode('');
                    setUseBackupCode(false);
                    completedTotpRef.current = '';
                    mfaSubmittingRef.current = false;
                    setSubmitError(null);
                  }}
                >
                  Back to password
                </button>
              </>
            )}

            <button type="submit" className="primary-button" disabled={loading}>
              {loading ? 'Signing in…' : loginStep === 'mfa' ? 'Verify MFA' : 'Sign in'}
            </button>
          </form>

          <p className="login-footnote">
            Need help? Enable MFA from the customer app security settings before admin sign-in.
          </p>
        </section>
      </main>
    </div>
  );
}
